import { useApolloClient, useMutation } from '@apollo/client/react'
import { device } from '@homemaster/jslib'
import * as ww from '@wecom/jssdk'
import { type PropsWithChildren, useCallback, useEffect, useState } from 'react'
import VConsole from 'vconsole'
import { getAppInfo } from '../config'
import { GetCurrentUserDocument, UserLoginAdminDocument } from '../gql/graphql'
import HomePage from '../HomePage'
import { type SiteInfo, SiteProvider } from '../hooks'
import { getAgentConfigSignature } from '../wxwork'

function registerWxwork() {
  const appInfo = getAppInfo()

  if (!appInfo.wxwork_corpid || !appInfo.wxwork_agentid) {
    console.error('wxwork_corpid or wxwork_agentid is missing in appInfo')
    throw new Error('WxWork configuration is incomplete')
  }

  ww.register({
    corpId: appInfo.wxwork_corpid,
    agentId: appInfo.wxwork_agentid,
    getAgentConfigSignature,
    jsApiList: [
      'getCurExternalContact',
      'getCurExternalChat',
      'getContext',
      'sendChatMessage',
    ],
  })
}

/**
 * Renders the children only after ensuring the user is logged in.
 *
 * If no saved login information is found, it shows a QR code login dialog.
 */
export default function LoginRedirector({ children }: PropsWithChildren) {
  const apolloClient = useApolloClient()
  const [state, setState] = useState<string>()
  const [sites, setSites] = useState<SiteInfo[]>([])
  const [login] = useMutation(UserLoginAdminDocument)

  useEffect(() => {
    const deviceType = device.getType()

    if (deviceType === 'wxwork') {
      const urlParams = new URLSearchParams(window.location.search)
      const authToken = urlParams.get('auth_token')
      const appInfo = getAppInfo()

      if (appInfo.deployEnv === 'dev' || appInfo.deployEnv === 'staging') {
        new VConsole()
      }

      if (authToken) {
        // removes the auth_token from
        const url = new URL(window.location.href)
        url.search = ''
        history.replaceState(null, '', url.href)
        localStorage.setItem('authToken', authToken)

        ;(async () => {
          try {
            const { data } = await login()

            // Step 1: Narrow out the 'undefined' or 'null' data case
            if (!data?.loginAdmin) {
              console.error('Login failed: No data received')
              setState('error')
              return
            }

            // At this point, tsc knows 'data.loginAdmin' definitely exists.
            const result = data.loginAdmin

            // Step 2: Narrow out the Error case
            if (result.__typename === 'GraphqlRequestError') {
              console.error('Login failed:', result.message)
              setState('error')
              return
            }

            // SUCCESS: TypeScript now knows 'result' MUST be 'LoginAdminResponse'.
            localStorage.setItem('authToken', result.authToken)
            setSites(result.user.sites)

            apolloClient.writeQuery({
              query: GetCurrentUserDocument,
              data: {
                __typename: 'Query',
                // biome-ignore lint/suspicious/noExplicitAny: writeQuery type doesn't accept SiteAdmin
                currentUser: result.user as any,
              },
            })
            registerWxwork()
            setState('ready')
          } catch (error) {
            console.error('Login error:', error)
            setState('error')
          }
        })()
      } else {
        setState('error')
      }
    } else {
      const authToken = localStorage.getItem('authToken')

      if (authToken) {
        ;(async () => {
          try {
            const { data } = await login()

            // Check if login failed or returned an error
            if (
              !data?.loginAdmin ||
              data.loginAdmin.__typename === 'GraphqlRequestError'
            ) {
              const errorMsg =
                data?.loginAdmin?.__typename === 'GraphqlRequestError'
                  ? data.loginAdmin.message
                  : 'No data received'
              console.error('Login failed:', errorMsg)
              localStorage.removeItem('authToken')
              setState('qrlogin')
              return
            }

            const loginResponse = data.loginAdmin
            localStorage.setItem('authToken', loginResponse.authToken)
            setSites(loginResponse.user.sites)

            apolloClient.writeQuery({
              query: GetCurrentUserDocument,
              data: {
                __typename: 'Query',
                // biome-ignore lint/suspicious/noExplicitAny: writeQuery type doesn't accept SiteAdmin
                currentUser: loginResponse.user as any,
              },
            })
            setState('ready')
          } catch (error) {
            console.error('Login error:', error)
            localStorage.removeItem('authToken')
            setState('qrlogin')
          }
        })()
      } else {
        setState('qrlogin')
      }
    }
  }, [login, apolloClient])

  const onLoginSuccess = useCallback((sites: SiteInfo[]) => {
    setSites(sites)
    setState('ready')
  }, [])

  switch (state) {
    case 'qrlogin':
      return <HomePage onLoginSuccess={onLoginSuccess} />
    case 'ready':
      return <SiteProvider sites={sites}>{children}</SiteProvider>
    case 'error':
      return <div>无法登陆</div>
    case undefined:
      return null
    default:
      throw new Error('Invalid LoginRedirector state')
  }
}
